import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Download, Pencil, Printer, CheckCircle, ArrowCounterClockwise, Bank } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PayrollPage = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);
  // Process Salary state
  const [accounts, setAccounts] = useState([]);
  const [processStatus, setProcessStatus] = useState(null);
  const [processOpen, setProcessOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [procForm, setProcForm] = useState({
    account_id: '',
    paid_date: new Date().toISOString().slice(0, 10),
    notes: '',
  });

  useEffect(() => {
    loadPayrolls();
    loadEmployees();
    loadMasters();
    loadAccounts();
  }, []);

  useEffect(() => {
    loadPayrolls();
    loadProcessStatus();
  }, [month]);

  const loadMasters = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/designations`, { withCredentials: true }),
      ]);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
    } catch (error) {
      console.error('Error loading masters:', error);
    }
  };

  const loadPayrolls = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payroll?month=${month}`, { withCredentials: true });
      setPayrolls(res.data);
    } catch (error) {
      console.error('Error loading payrolls:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`, { withCredentials: true });
      setEmployees(res.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true });
      setAccounts((res.data || []).filter((a) => a.is_active !== false));
    } catch (error) {
      // Non-admins won't have access — silently ignore so the page still works for view-only users
      setAccounts([]);
    }
  };

  const loadProcessStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payroll/status?month=${month}`, { withCredentials: true });
      setProcessStatus(res.data);
    } catch (error) {
      setProcessStatus(null);
    }
  };

  const openProcessDialog = () => {
    setProcForm({
      account_id: processStatus?.account_id || (accounts[0]?.id || ''),
      paid_date: processStatus?.paid_date || new Date().toISOString().slice(0, 10),
      notes: '',
    });
    setProcessOpen(true);
  };

  const handleProcessSalary = async () => {
    if (!procForm.account_id) {
      alert('Please select the bank/cash account to debit.');
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/payroll/process`,
        { month, ...procForm },
        { withCredentials: true },
      );
      setProcessOpen(false);
      await Promise.all([loadPayrolls(), loadProcessStatus()]);
      alert(
        `Salary processed — ₹${Number(res.data.total_net_salary || 0).toLocaleString('en-IN')} debited from ${res.data.account_nickname}.`,
      );
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Failed to process';
      alert(`Process Salary failed: ${detail}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleReverseSalary = async () => {
    if (!processStatus?.is_processed) return;
    const confirmMsg = `Reverse processed salary for ${month}?\n\nThis will:\n• Delete the Salaries Paid expense row (₹${Number(processStatus.expense_amount || 0).toLocaleString('en-IN')})\n• Roll back the Cash Book debit from ${processStatus.account_nickname || 'the bank account'}\n• Reset all ${processStatus.paid_count} payrolls back to pending\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.post(
        `${API_URL}/api/payroll/reverse`,
        { month },
        { withCredentials: true },
      );
      await Promise.all([loadPayrolls(), loadProcessStatus()]);
      alert('Salary processing reversed. The month is back to pending.');
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || 'Failed to reverse';
      alert(`Reverse failed: ${detail}`);
    }
  };

  const handleGeneratePayroll = async () => {
    const attendanceData = JSON.parse(localStorage.getItem('attendance_data') || '[]');
    setLoading(true);
    try {
      // Prefer daily attendance grid; fall back to imported CSV if a user just uploaded one.
      const payload = attendanceData.length > 0
        ? { month, attendance_data: attendanceData }
        : { month, use_daily: true };
      await axios.post(`${API_URL}/api/payroll/generate`, payload, { withCredentials: true });
      localStorage.removeItem('attendance_data');
      loadPayrolls();
      alert('Payroll generated successfully!');
    } catch (error) {
      console.error('Error generating payroll:', error);
      alert('Error generating payroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (payroll) => {
    setCurrentEdit({ ...payroll });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!currentEdit?.id) {
      toast.error('This payroll row is missing its ID. Click Cancel, refresh the page, and try again.');
      return;
    }
    try {
      const earned = Number(currentEdit.earned_salary) || 0;
      const epf = Number(currentEdit.epf) || 0;
      const esi = Number(currentEdit.esi) || 0;
      const tds = Number(currentEdit.tds) || 0;
      const inc = Number(currentEdit.incentives) || 0;
      const updateData = {
        earned_salary: earned,
        epf, esi, tds, incentives: inc,
        total_deductions: epf + esi + tds,
        net_salary: earned - (epf + esi + tds) + inc,
        manual_override: true,
      };
      await axios.put(`${API_URL}/api/payroll/${currentEdit.id}`, updateData, { withCredentials: true });
      toast.success('Payroll override saved');
      setEditOpen(false);
      loadPayrolls();
    } catch (error) {
      const status = error.response?.status;
      let detail = error.response?.data?.detail || error.message;
      if (typeof detail !== 'string') detail = JSON.stringify(detail);
      if (status === 405) {
        detail = 'Backend rejected the update (Method Not Allowed). On production, run `bash deploy/deploy-full.sh` to restart the backend.';
      }
      toast.error('Save failed: ' + detail);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payroll/export/${month}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `salary_${month}.csv`;
      a.click();
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const numberToIndianWords = (num) => {
    if (num === 0) return 'Zero';
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
      if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
      return '';
    };
    let n = Math.round(num);
    let out = '';
    const crore = Math.floor(n / 10000000); n %= 10000000;
    const lakh = Math.floor(n / 100000); n %= 100000;
    const thousand = Math.floor(n / 1000); n %= 1000;
    if (crore) out += inWords(crore) + ' Crore ';
    if (lakh) out += inWords(lakh) + ' Lakh ';
    if (thousand) out += inWords(thousand) + ' Thousand ';
    if (n) out += inWords(n);
    return out.trim();
  };

  const formatMonthLabel = (ym) => {
    // ym: '2026-04' -> 'April 2026'
    try {
      const [y, m] = ym.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1)
        .toLocaleString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return ym;
    }
  };

  const printSalarySlip = (payroll) => {
    const emp = employees.find((e) => e.id === payroll.employee_id);
    if (!emp) return;
    const dept = departments.find((d) => d.id === emp.department_id);
    const desig = designations.find((d) => d.id === emp.designation_id);
    const sc = emp.salary_components || {};
    const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const actualDays = payroll.actual_days || 0;
    const daysWorked = payroll.days_worked || 0;
    const lopDays = Math.max(0, actualDays - daysWorked);

    // Earnings prorated by days worked
    const ratio = actualDays > 0 ? daysWorked / actualDays : 1;
    const earn = {
      basic: (sc.basic || 0) * ratio,
      hra: (sc.hra || 0) * ratio,
      con: (sc.con || 0) * ratio,
      medical: (sc.medical || 0) * ratio,
      non_taxable: (sc.non_taxable || 0) * ratio,
      special_allowance: (sc.special_allowance || 0) * ratio,
    };
    const totalEarnings = Object.values(earn).reduce((a, b) => a + b, 0) + (payroll.incentives || 0);
    const totalDeductions = payroll.total_deductions || 0;
    const netPay = totalEarnings - totalDeductions;

    const printWindow = window.open('', '', 'width=900,height=700');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head>
        <title>Pay Slip - ${emp.name} - ${formatMonthLabel(month)}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          * { box-sizing: border-box; }
          body { font-family: 'IBM Plex Sans', Arial, sans-serif; color:#1a1f2e; font-size:12px; margin:0; padding:0; }
          .slip { width: 100%; }
          .top { display:flex; align-items:center; justify-content:space-between; padding-bottom:10px; border-bottom:2px solid #1D9E75; }
          .top .left img { height: 44px; }
          .top .right { text-align:right; }
          .top h2 { margin:0; font-family: 'Chivo', sans-serif; font-size:18px; letter-spacing:1px; color:#1a1f2e; }
          .top .sub { color:#6b7280; font-size:11px; margin-top:2px; }
          .info { display:grid; grid-template-columns: 1fr 1fr; gap:6px 22px; padding:14px 0; border-bottom:1px solid #e5e7eb; }
          .info .row { display:flex; }
          .info .row .k { width:140px; color:#6b7280; font-weight:500; }
          .info .row .v { color:#1a1f2e; font-weight:600; }
          table.t { width:100%; border-collapse:collapse; margin-top:14px; }
          table.t th { background:#f0f2f7; color:#1a1f2e; text-align:left; padding:8px 10px; font-size:11px; text-transform:uppercase; letter-spacing:0.5px; border-bottom:1px solid #d1d5db; }
          table.t th.r, table.t td.r { text-align:right; }
          table.t td { padding:7px 10px; border-bottom:1px solid #e5e7eb; }
          .grid2 { display:grid; grid-template-columns: 1fr 1fr; gap:14px; }
          .totals td { background:#f0f2f7; font-weight:700; }
          .netpay { background:#1D9E75; color:#fff; padding:12px 16px; margin-top:14px; display:flex; justify-content:space-between; align-items:center; border-radius:4px; }
          .netpay .lbl { font-size:13px; letter-spacing:0.5px; }
          .netpay .val { font-family:'Chivo', sans-serif; font-size:20px; font-weight:700; }
          .inwords { padding:10px 12px; margin-top:6px; background:#fafbfc; border:1px dashed #d1d5db; font-size:11px; color:#374151; }
          .sign { margin-top:32px; display:flex; justify-content:space-between; padding-top:10px; }
          .sign .box { width:40%; text-align:center; border-top:1px solid #6b7280; padding-top:6px; font-size:11px; color:#6b7280; }
          .foot { margin-top:18px; font-size:10px; color:#9ca3af; text-align:center; }
        </style>
      </head><body>
        <div class="slip">
          <div class="top">
            <div class="left">
              <img src="https://customer-assets.emergentagent.com/job_6cece927-fe7d-443b-b094-47167d0b0812/artifacts/cfgq9z4h_logo-v8-black.png" alt="logo" />
            </div>
            <div class="right">
              <h2>PAY SLIP</h2>
              <div class="sub">For the Month of ${formatMonthLabel(month)}</div>
            </div>
          </div>

          <div class="info">
            <div class="row"><div class="k">Employee Name</div><div class="v">${emp.name || '-'}</div></div>
            <div class="row"><div class="k">Employee ID</div><div class="v">${emp.emp_id || '-'}</div></div>
            <div class="row"><div class="k">Department</div><div class="v">${dept?.name || '-'}</div></div>
            <div class="row"><div class="k">Designation</div><div class="v">${desig?.name || '-'}</div></div>
            <div class="row"><div class="k">Date of Joining</div><div class="v">${(() => { const s = String(emp.joining_date || '').slice(0,10); const [y,m,d] = s.split('-'); return y ? `${d}/${m}/${y}` : (emp.joining_date || '-'); })()}</div></div>
            <div class="row"><div class="k">PAN No.</div><div class="v">${emp.pan_no || '-'}</div></div>
            <div class="row"><div class="k">Bank A/C No.</div><div class="v">${emp.bank_account || '-'}</div></div>
            <div class="row"><div class="k">IFSC Code</div><div class="v">${emp.bank_ifsc || '-'}</div></div>
            <div class="row"><div class="k">Working Days</div><div class="v">${actualDays}</div></div>
            <div class="row"><div class="k">Days Paid</div><div class="v">${daysWorked}</div></div>
            <div class="row"><div class="k">LOP Days</div><div class="v">${lopDays}</div></div>
            <div class="row"><div class="k">Payment Mode</div><div class="v">${emp.payment_type || 'IFT'}</div></div>
          </div>

          <div class="grid2">
            <table class="t">
              <thead><tr><th>Earnings</th><th class="r">Amount (₹)</th></tr></thead>
              <tbody>
                <tr><td>Basic</td><td class="r">${fmt(earn.basic)}</td></tr>
                <tr><td>HRA</td><td class="r">${fmt(earn.hra)}</td></tr>
                <tr><td>Conveyance</td><td class="r">${fmt(earn.con)}</td></tr>
                <tr><td>Medical</td><td class="r">${fmt(earn.medical)}</td></tr>
                <tr><td>Non-Taxable</td><td class="r">${fmt(earn.non_taxable)}</td></tr>
                <tr><td>Special Allowance</td><td class="r">${fmt(earn.special_allowance)}</td></tr>
                <tr><td>Incentives</td><td class="r">${fmt(payroll.incentives || 0)}</td></tr>
                <tr class="totals"><td>Total Earnings</td><td class="r">${fmt(totalEarnings)}</td></tr>
              </tbody>
            </table>

            <table class="t">
              <thead><tr><th>Deductions</th><th class="r">Amount (₹)</th></tr></thead>
              <tbody>
                <tr><td>EPF (12% Basic)</td><td class="r">${fmt(payroll.epf || 0)}</td></tr>
                <tr><td>ESI (0.75%)</td><td class="r">${fmt(payroll.esi || 0)}</td></tr>
                <tr><td>TDS</td><td class="r">${fmt(payroll.tds || 0)}</td></tr>
                <tr><td>Professional Tax</td><td class="r">0.00</td></tr>
                <tr><td>Loan / Advance</td><td class="r">0.00</td></tr>
                <tr><td>Other</td><td class="r">0.00</td></tr>
                <tr><td>&nbsp;</td><td class="r">&nbsp;</td></tr>
                <tr class="totals"><td>Total Deductions</td><td class="r">${fmt(totalDeductions)}</td></tr>
              </tbody>
            </table>
          </div>

          <div class="netpay">
            <div class="lbl">NET PAY</div>
            <div class="val">₹ ${fmt(netPay)}</div>
          </div>

          <div class="inwords">
            <strong>In Words:</strong> Indian Rupees ${numberToIndianWords(netPay)} Only
          </div>

          <div class="sign">
            <div class="box">Employee Signature</div>
            <div class="box">Authorised Signatory</div>
          </div>

          <div class="foot">
            This is a system-generated pay slip and does not require a physical signature.
          </div>
        </div>
        <script>window.onload = function(){ window.print(); };</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div data-testid="payroll-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Payroll Management
          </h1>
          <p className="text-[#52525B] text-sm">Generate and manage monthly payroll</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={handleGeneratePayroll}
            disabled={loading || processStatus?.is_processed}
            data-testid="generate-payroll-btn"
            className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-sm disabled:opacity-50"
            title={processStatus?.is_processed ? 'Reverse the processed salary first to re-generate' : ''}
          >
            <Plus size={20} className="mr-2" />
            {loading ? 'Generating...' : 'Generate Payroll'}
          </Button>
          {payrolls.length > 0 && (
            <>
              {!processStatus?.is_processed ? (
                <Button
                  onClick={openProcessDialog}
                  data-testid="process-salary-btn"
                  className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
                >
                  <CheckCircle size={20} className="mr-2" />
                  Process Salary
                </Button>
              ) : (
                <>
                  <Button
                    onClick={openProcessDialog}
                    data-testid="reprocess-salary-btn"
                    variant="outline"
                    className="rounded-sm border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75]/10"
                  >
                    <CheckCircle size={20} className="mr-2" />
                    Re-process
                  </Button>
                  <Button
                    onClick={handleReverseSalary}
                    data-testid="reverse-salary-btn"
                    variant="outline"
                    className="rounded-sm border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
                  >
                    <ArrowCounterClockwise size={20} className="mr-2" />
                    Reverse
                  </Button>
                </>
              )}
              <Button
                onClick={handleExportCSV}
                data-testid="export-csv-btn"
                className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
              >
                <Download size={20} className="mr-2" />
                Export Bank CSV
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <Label>Select Month</Label>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-64" data-testid="month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() - i);
              return d.toISOString().slice(0, 7);
            }).map((m) => (
              <SelectItem key={m} value={m}>
                {new Date(m).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-[#E4E4E7]">
        {processStatus?.is_processed && (
          <div
            data-testid="salary-processed-banner"
            className="px-5 py-4 border-b border-[#1D9E75]/20 bg-[#1D9E75]/5 flex items-center justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-3">
              <CheckCircle size={22} weight="fill" className="text-[#1D9E75]" />
              <div>
                <div className="text-sm font-semibold text-[#1a1f2e]">
                  Salary processed for {formatMonthLabel(month)}
                </div>
                <div className="text-xs text-[#52525B] tabular-nums">
                  ₹{Number(processStatus.expense_amount || 0).toLocaleString('en-IN')} debited from{' '}
                  <span className="font-medium">{processStatus.account_nickname || '—'}</span> on{' '}
                  {processStatus.paid_date || '—'} · {processStatus.paid_count} employees
                </div>
              </div>
            </div>
            <div className="text-xs text-[#1D9E75] font-medium">
              ✓ Expense + Cash Book entry posted · reflects in Profit &amp; Loss
            </div>
          </div>
        )}
        {!processStatus?.is_processed && payrolls.length > 0 && (
          <div
            data-testid="salary-pending-banner"
            className="px-5 py-3 border-b border-[#E4E4E7] bg-[#FFF8E1] text-xs text-[#92400E] flex items-center justify-between gap-4 flex-wrap"
          >
            <span>
              Salary not yet processed · ₹{Number(processStatus?.total_net_salary || 0).toLocaleString('en-IN')} pending
              across {processStatus?.total_payrolls || 0} payrolls
            </span>
            <span className="font-medium">Click &ldquo;Process Salary&rdquo; to post the Cash Book debit &amp; create the P&amp;L expense.</span>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F5]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Employee</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Days</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Earned Salary</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Deductions</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Net Salary</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payrolls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-[#52525B]">
                  No payroll records found. Import attendance and generate payroll.
                </TableCell>
              </TableRow>
            ) : (
              payrolls.map((payroll) => {
                const emp = employees.find((e) => e.id === payroll.employee_id);
                return (
                  <TableRow key={payroll.id} data-testid={`payroll-row-${payroll.id}`}>
                    <TableCell className="font-medium">
                      {emp ? (
                        <div>
                          <div>{emp.name}</div>
                          <div className="text-xs text-[#52525B]">{emp.emp_id}</div>
                        </div>
                      ) : (
                        'Unknown'
                      )}
                    </TableCell>
                    <TableCell>
                      {payroll.days_worked}/{payroll.actual_days}
                    </TableCell>
                    <TableCell className="font-semibold">₹{payroll.earned_salary.toLocaleString()}</TableCell>
                    <TableCell className="text-[#DC2626]">₹{payroll.total_deductions.toLocaleString()}</TableCell>
                    <TableCell className="font-bold text-[#16A34A]">₹{payroll.net_salary.toLocaleString()}</TableCell>
                    <TableCell>
                      {payroll.status === 'paid' ? (
                        <span
                          data-testid={`status-paid-${payroll.id}`}
                          className="px-2 py-1 text-xs rounded-sm bg-[#1D9E75]/10 text-[#0F6E56] font-medium"
                          title={`Paid on ${payroll.processed_paid_date || ''} via ${payroll.processed_account_nickname || ''}`}
                        >
                          Paid
                        </span>
                      ) : (
                        <span
                          className={`px-2 py-1 text-xs rounded-sm ${
                            payroll.manual_override ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {payroll.manual_override ? 'Modified' : 'Auto'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(payroll)}
                          data-testid={`edit-payroll-${payroll.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printSalarySlip(payroll)}
                          data-testid={`print-slip-${payroll.id}`}
                        >
                          <Printer size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Override - Payroll</DialogTitle>
          </DialogHeader>
          {currentEdit && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-sm text-sm">
                <strong>Note:</strong> Manual overrides will be marked and saved separately.
              </div>
              <div className="space-y-2">
                <Label>Earned Salary</Label>
                <Input
                  type="number"
                  value={currentEdit.earned_salary}
                  onChange={(e) =>
                    setCurrentEdit({ ...currentEdit, earned_salary: parseFloat(e.target.value) || 0 })
                  }
                  data-testid="override-earned-salary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>EPF</Label>
                  <Input
                    type="number"
                    value={currentEdit.epf}
                    onChange={(e) => setCurrentEdit({ ...currentEdit, epf: parseFloat(e.target.value) || 0 })}
                    data-testid="override-epf"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ESI</Label>
                  <Input
                    type="number"
                    value={currentEdit.esi}
                    onChange={(e) => setCurrentEdit({ ...currentEdit, esi: parseFloat(e.target.value) || 0 })}
                    data-testid="override-esi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>TDS</Label>
                  <Input
                    type="number"
                    value={currentEdit.tds}
                    onChange={(e) => setCurrentEdit({ ...currentEdit, tds: parseFloat(e.target.value) || 0 })}
                    data-testid="override-tds"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Incentives</Label>
                  <Input
                    type="number"
                    value={currentEdit.incentives}
                    onChange={(e) =>
                      setCurrentEdit({ ...currentEdit, incentives: parseFloat(e.target.value) || 0 })
                    }
                    data-testid="override-incentives"
                  />
                </div>
              </div>
              <div className="p-4 bg-[#F4F4F5] rounded-sm">
                <p className="text-sm font-semibold">
                  New Net Salary: ₹
                  {(
                    currentEdit.earned_salary -
                    (currentEdit.epf + currentEdit.esi + currentEdit.tds) +
                    currentEdit.incentives
                  ).toLocaleString()}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-override-btn">
                  Save Override
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={processOpen} onOpenChange={setProcessOpen}>
        <DialogContent className="max-w-md" data-testid="process-salary-dialog">
          <DialogHeader>
            <DialogTitle>
              {processStatus?.is_processed ? 'Re-process Salary' : 'Process Salary'} —{' '}
              {formatMonthLabel(month)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#F4F4F5] rounded-sm space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-[#52525B]">Total Net Salary</span>
                <span className="font-bold text-[#1a1f2e] tabular-nums">
                  ₹{Number(processStatus?.total_net_salary || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex justify-between text-xs text-[#52525B]">
                <span>Employees</span>
                <span className="tabular-nums">{processStatus?.total_payrolls || 0}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>
                Pay From Account <span className="text-red-500">*</span>
              </Label>
              <Select
                value={procForm.account_id}
                onValueChange={(v) => setProcForm({ ...procForm, account_id: v })}
              >
                <SelectTrigger data-testid="process-account-select">
                  <SelectValue placeholder="Choose bank / cash account…" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#52525B]">
                      No bank accounts. Add one from Cash Book → Bank Accounts.
                    </div>
                  ) : (
                    accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id} data-testid={`process-account-opt-${a.id}`}>
                        <span className="flex items-center gap-2">
                          <Bank size={14} className="text-[#1D9E75]" />
                          {a.nickname}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#52525B]">
                A consolidated &ldquo;Salaries Paid&rdquo; expense + debit entry will be posted to this account.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={procForm.paid_date}
                onChange={(e) => setProcForm({ ...procForm, paid_date: e.target.value })}
                data-testid="process-paid-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Salary credit for the month"
                value={procForm.notes}
                onChange={(e) => setProcForm({ ...procForm, notes: e.target.value })}
                data-testid="process-notes"
              />
            </div>
            {processStatus?.is_processed && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-sm text-xs text-orange-900">
                <strong>Re-processing</strong> will delete the existing salary expense &amp; ledger entry and
                create them again with the new details.
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setProcessOpen(false)} data-testid="process-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleProcessSalary}
                disabled={processing || !procForm.account_id || accounts.length === 0}
                data-testid="process-confirm-btn"
                className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white"
              >
                {processing
                  ? 'Posting…'
                  : processStatus?.is_processed
                  ? 'Re-post Entries'
                  : 'Confirm & Post'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
